const API_BASE_URL = import.meta.env.PROD
  ? '/api'
  : 'http://localhost:3000/api';

interface RequestOptions extends RequestInit {
  skipAuth?: boolean;
}

class ApiClient {
  private getAccessToken(): string | null {
    return localStorage.getItem('accessToken');
  }

  private getRefreshToken(): string | null {
    return localStorage.getItem('refreshToken');
  }

  private setTokens(accessToken: string, refreshToken: string): void {
    localStorage.setItem('accessToken', accessToken);
    localStorage.setItem('refreshToken', refreshToken);
  }

  clearTokens(): void {
    localStorage.removeItem('accessToken');
    localStorage.removeItem('refreshToken');
  }

  private async refreshAccessToken(): Promise<boolean> {
    const refreshToken = this.getRefreshToken();
    if (!refreshToken) return false;

    try {
      const response = await fetch(`${API_BASE_URL}/auth/refresh`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ refreshToken }),
      });

      if (!response.ok) {
        this.clearTokens();
        return false;
      }

      const data = await response.json();
      this.setTokens(data.accessToken, data.refreshToken);
      return true;
    } catch {
      this.clearTokens();
      return false;
    }
  }

  async request<T>(endpoint: string, options: RequestOptions = {}): Promise<T> {
    const { skipAuth = false, ...fetchOptions } = options;

    const headers: HeadersInit = {
      'Content-Type': 'application/json',
      ...fetchOptions.headers,
    };

    if (!skipAuth) {
      const token = this.getAccessToken();
      if (token) {
        (headers as Record<string, string>)['Authorization'] = `Bearer ${token}`;
      }
    }

    let response = await fetch(`${API_BASE_URL}${endpoint}`, {
      ...fetchOptions,
      headers,
    });

    // If 401, try to refresh token
    if (response.status === 401 && !skipAuth) {
      const refreshed = await this.refreshAccessToken();
      if (refreshed) {
        const newToken = this.getAccessToken();
        (headers as Record<string, string>)['Authorization'] = `Bearer ${newToken}`;
        response = await fetch(`${API_BASE_URL}${endpoint}`, {
          ...fetchOptions,
          headers,
        });
      }
    }

    if (!response.ok) {
      const error = await response.json().catch(() => ({ error: 'Request failed' }));
      throw new Error(error.error || 'Request failed');
    }

    // Handle 204 No Content responses (e.g., DELETE requests)
    if (response.status === 204) {
      return undefined as T;
    }

    return response.json();
  }

  async login(email: string, password: string) {
    const data = await this.request<{
      accessToken: string;
      refreshToken: string;
      user: User;
    }>('/auth/login', {
      method: 'POST',
      body: JSON.stringify({ email, password }),
      skipAuth: true,
    });

    this.setTokens(data.accessToken, data.refreshToken);
    return data.user;
  }

  async logout(): Promise<void> {
    const refreshToken = this.getRefreshToken();
    try {
      await this.request('/auth/logout', {
        method: 'POST',
        body: JSON.stringify({ refreshToken }),
      });
    } finally {
      this.clearTokens();
    }
  }

  async getMe() {
    return this.request<User>('/auth/me');
  }

  // Users / Smoelenboek
  async getUsers(params?: { voiceGroup?: string; search?: string }) {
    const searchParams = new URLSearchParams();
    if (params?.voiceGroup) searchParams.set('voiceGroup', params.voiceGroup);
    if (params?.search) searchParams.set('search', params.search);
    const query = searchParams.toString();
    return this.request<UserListItem[]>(`/users${query ? `?${query}` : ''}`);
  }

  async updateProfile(data: { voiceGroup?: string | null }) {
    return this.request<User>('/users/me/profile', {
      method: 'PATCH',
      body: JSON.stringify(data),
    });
  }

  async uploadProfilePhoto(file: File) {
    const formData = new FormData();
    formData.append('photo', file);

    const token = this.getAccessToken();
    const response = await fetch(`${API_BASE_URL}/files/profile-photo`, {
      method: 'POST',
      headers: {
        Authorization: `Bearer ${token}`,
      },
      body: formData,
    });

    if (!response.ok) {
      const error = await response.json().catch(() => ({ error: 'Upload failed' }));
      throw new Error(error.error || 'Upload failed');
    }

    return response.json() as Promise<{ photo: string }>;
  }

  // Admin - User Management
  async getAdminUsers() {
    return this.request<AdminUser[]>('/users');
  }

  async createUser(data: CreateUserData) {
    return this.request<{ user: AdminUser; temporaryPassword: string }>('/users', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  async updateUser(userId: string, data: UpdateUserData) {
    return this.request<AdminUser>(`/users/${userId}`, {
      method: 'PATCH',
      body: JSON.stringify(data),
    });
  }

  async deleteUser(userId: string) {
    return this.request<{ message: string }>(`/users/${userId}`, {
      method: 'DELETE',
    });
  }

  async resetUserPassword(userId: string) {
    return this.request<{ message: string; temporaryPassword: string; emailSent: boolean }>(`/users/${userId}/reset-password`, {
      method: 'POST',
    });
  }

  async toggleTemporaryDirigent(userId: string) {
    return this.request<AdminUser>(`/users/${userId}/toggle-temporary-dirigent`, {
      method: 'POST',
    });
  }

  async uploadUserProfilePhoto(userId: string, file: File) {
    const formData = new FormData();
    formData.append('photo', file);

    const token = this.getAccessToken();
    const response = await fetch(`${API_BASE_URL}/files/profile-photo/${userId}`, {
      method: 'POST',
      headers: {
        Authorization: `Bearer ${token}`,
      },
      body: formData,
    });

    if (!response.ok) {
      const error = await response.json().catch(() => ({ error: 'Upload failed' }));
      throw new Error(error.error || 'Upload failed');
    }

    return response.json() as Promise<{ photo: string }>;
  }

  // ========== VOICE PART TEMPLATES ==========
  async getVoicePartTemplates() {
    return this.request<VoicePartTemplate[]>('/voice-part-templates');
  }

  async createVoicePartTemplate(data: { name: string; sortOrder?: number }) {
    return this.request<VoicePartTemplate>('/voice-part-templates', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  async updateVoicePartTemplate(templateId: string, data: { name?: string; sortOrder?: number }) {
    return this.request<VoicePartTemplate>(`/voice-part-templates/${templateId}`, {
      method: 'PATCH',
      body: JSON.stringify(data),
    });
  }

  async deleteVoicePartTemplate(templateId: string) {
    return this.request<void>(`/voice-part-templates/${templateId}`, {
      method: 'DELETE',
    });
  }

  // ========== SONGS ==========
  async getSongs(params?: { search?: string; tagId?: string; sortBy?: string; sortOrder?: string }) {
    const searchParams = new URLSearchParams();
    if (params?.search) searchParams.set('search', params.search);
    if (params?.tagId) searchParams.set('tagId', params.tagId);
    if (params?.sortBy) searchParams.set('sortBy', params.sortBy);
    if (params?.sortOrder) searchParams.set('sortOrder', params.sortOrder);
    const query = searchParams.toString();
    return this.request<Song[]>(`/songs${query ? `?${query}` : ''}`);
  }

  async getSong(songId: string) {
    return this.request<SongDetail>(`/songs/${songId}`);
  }

  async createSong(data: CreateSongData) {
    return this.request<Song>('/songs', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  async updateSong(songId: string, data: Partial<CreateSongData>) {
    return this.request<Song>(`/songs/${songId}`, {
      method: 'PATCH',
      body: JSON.stringify(data),
    });
  }

  async deleteSong(songId: string) {
    return this.request<void>(`/songs/${songId}`, {
      method: 'DELETE',
    });
  }

  // Voice Parts
  async addVoicePart(songId: string, data: { name: string; sortOrder?: number }) {
    return this.request<VoicePart>(`/songs/${songId}/voice-parts`, {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  async deleteVoicePart(songId: string, partId: string) {
    return this.request<void>(`/songs/${songId}/voice-parts/${partId}`, {
      method: 'DELETE',
    });
  }

  async selectVoicePart(songId: string, voicePartId: string) {
    return this.request<{ voicePartId: string }>(`/songs/${songId}/select-voice`, {
      method: 'POST',
      body: JSON.stringify({ voicePartId }),
    });
  }

  // Bookmarks (Markeringen) - alleen MC+ kan beheren
  async addBookmark(songId: string, data: { label?: string; name: string; timestamp: number; omitPreRoll?: boolean; omitFadeIn?: boolean }) {
    return this.request<Bookmark>(`/songs/${songId}/bookmarks`, {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  async deleteBookmark(songId: string, bookmarkId: string) {
    return this.request<void>(`/songs/${songId}/bookmarks/${bookmarkId}`, {
      method: 'DELETE',
    });
  }

  async updateBookmark(songId: string, bookmarkId: string, data: { label?: string; name?: string; timestamp?: number; omitPreRoll?: boolean; omitFadeIn?: boolean }) {
    return this.request<Bookmark>(`/songs/${songId}/bookmarks/${bookmarkId}`, {
      method: 'PATCH',
      body: JSON.stringify(data),
    });
  }

  // ========== TAGS ==========
  async getTags() {
    return this.request<Tag[]>('/tags');
  }

  async createTag(data: { name: string; color?: string }) {
    return this.request<Tag>('/tags', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  async updateTag(tagId: string, data: { name?: string; color?: string }) {
    return this.request<Tag>(`/tags/${tagId}`, {
      method: 'PATCH',
      body: JSON.stringify(data),
    });
  }

  async deleteTag(tagId: string) {
    return this.request<void>(`/tags/${tagId}`, {
      method: 'DELETE',
    });
  }

  // ========== FILES ==========
  async uploadFile(songId: string, file: File, type: FileType, voicePartId?: string) {
    const formData = new FormData();
    formData.append('file', file);
    formData.append('type', type);
    if (voicePartId) formData.append('voicePartId', voicePartId);

    const token = this.getAccessToken();
    const response = await fetch(`${API_BASE_URL}/files/upload/${songId}`, {
      method: 'POST',
      headers: { Authorization: `Bearer ${token}` },
      body: formData,
    });

    if (!response.ok) {
      const error = await response.json().catch(() => ({ error: 'Upload failed' }));
      throw new Error(error.error || 'Upload failed');
    }

    return response.json() as Promise<SongFile>;
  }

  async deleteFile(fileId: string) {
    return this.request<void>(`/files/${fileId}`, {
      method: 'DELETE',
    });
  }

  async updateFile(fileId: string, data: { voicePartId?: string | null }) {
    return this.request<SongFile>(`/files/${fileId}`, {
      method: 'PATCH',
      body: JSON.stringify(data),
    });
  }

  async uploadSongCover(songId: string, file: File) {
    const formData = new FormData();
    formData.append('cover', file);

    const token = this.getAccessToken();
    const response = await fetch(`${API_BASE_URL}/files/song-cover/${songId}`, {
      method: 'POST',
      headers: {
        Authorization: `Bearer ${token}`,
      },
      body: formData,
    });

    if (!response.ok) {
      const error = await response.json().catch(() => ({ error: 'Upload failed' }));
      throw new Error(error.error || 'Upload failed');
    }

    return response.json() as Promise<{ coverImage: string }>;
  }

  async deleteSongCover(songId: string) {
    return this.request<void>(`/files/song-cover/${songId}`, {
      method: 'DELETE',
    });
  }

  getFileUrl(fileId: string) {
    return `${API_BASE_URL}/files/${fileId}`;
  }

  // Get file URL with authentication token for audio/video elements
  getAuthenticatedFileUrl(fileId: string): string {
    const token = this.getAccessToken();
    return `${API_BASE_URL}/files/${fileId}${token ? `?token=${token}` : ''}`;
  }

  // Get download URL with authentication token
  getDownloadUrl(fileId: string): string {
    const token = this.getAccessToken();
    return `${API_BASE_URL}/files/${fileId}/download${token ? `?token=${token}` : ''}`;
  }

  // Voice Parts - add from template
  async addVoicePartFromTemplate(songId: string, templateId: string) {
    // Get template name first
    const templates = await this.getVoicePartTemplates();
    const template = templates.find(t => t.id === templateId);
    if (!template) throw new Error('Template niet gevonden');

    return this.request<VoicePart>(`/songs/${songId}/voice-parts`, {
      method: 'POST',
      body: JSON.stringify({
        name: template.name,
        templateId: templateId,
      }),
    });
  }

  // ========== USER TAGS ==========
  async getUserTags() {
    return this.request<UserTag[]>('/user-tags');
  }

  async createUserTag(data: { name: string; color?: string; sortOrder?: number }) {
    return this.request<UserTag>('/user-tags', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  async updateUserTag(id: string, data: { name?: string; color?: string; sortOrder?: number }) {
    return this.request<UserTag>(`/user-tags/${id}`, {
      method: 'PATCH',
      body: JSON.stringify(data),
    });
  }

  async deleteUserTag(id: string) {
    return this.request<void>(`/user-tags/${id}`, {
      method: 'DELETE',
    });
  }

  async setUserTags(userId: string, tagIds: string[]) {
    return this.request<UserListItem>(`/user-tags/user/${userId}`, {
      method: 'PUT',
      body: JSON.stringify({ tagIds }),
    });
  }

  // ========== IMPORT ==========
  async importPreview(file: File): Promise<ImportPreviewResponse> {
    const formData = new FormData();
    formData.append('file', file);

    const token = localStorage.getItem('accessToken');
    const response = await fetch(`${API_BASE_URL}/import/preview`, {
      method: 'POST',
      headers: token ? { Authorization: `Bearer ${token}` } : {},
      body: formData,
    });

    if (!response.ok) {
      const error = await response.json().catch(() => ({ error: 'Upload failed' }));
      throw new Error(error.error || 'Upload failed');
    }

    return response.json();
  }

  async importExecute(items: ImportItem[]): Promise<ImportResult> {
    return this.request<ImportResult>('/import/execute', {
      method: 'POST',
      body: JSON.stringify({ items }),
    });
  }

  getImportTemplateUrl(): string {
    return `${API_BASE_URL}/import/template`;
  }

  // ========== ANYTUNE IMPORT ==========
  async anytunePreview(songId: string, file: File): Promise<AnytunePreviewResponse> {
    const formData = new FormData();
    formData.append('file', file);

    const token = localStorage.getItem('accessToken');
    const response = await fetch(`${API_BASE_URL}/songs/${songId}/import-markers/preview`, {
      method: 'POST',
      headers: token ? { Authorization: `Bearer ${token}` } : {},
      body: formData,
    });

    if (!response.ok) {
      const error = await response.json().catch(() => ({ error: 'Upload failed' }));
      throw new Error(error.error || 'Upload failed');
    }

    return response.json();
  }

  async anytuneExecute(songId: string, markers: AnytuneMarker[]): Promise<{ success: boolean; created: number }> {
    return this.request<{ success: boolean; created: number }>(`/songs/${songId}/import-markers/execute`, {
      method: 'POST',
      body: JSON.stringify({ markers }),
    });
  }

  // ========== PLAYLISTS ==========
  async getPlaylists() {
    return this.request<Playlist[]>('/playlists');
  }

  async getPlaylist(playlistId: string) {
    return this.request<Playlist>(`/playlists/${playlistId}`);
  }

  async createPlaylist(name: string) {
    return this.request<Playlist>('/playlists', {
      method: 'POST',
      body: JSON.stringify({ name }),
    });
  }

  async updatePlaylist(playlistId: string, name: string) {
    return this.request<Playlist>(`/playlists/${playlistId}`, {
      method: 'PATCH',
      body: JSON.stringify({ name }),
    });
  }

  async deletePlaylist(playlistId: string) {
    return this.request<void>(`/playlists/${playlistId}`, {
      method: 'DELETE',
    });
  }

  async addToPlaylist(playlistId: string, songId: string, voicePartId: string) {
    return this.request<PlaylistItem>(`/playlists/${playlistId}/items`, {
      method: 'POST',
      body: JSON.stringify({ songId, voicePartId }),
    });
  }

  async removeFromPlaylist(playlistId: string, itemId: string) {
    return this.request<void>(`/playlists/${playlistId}/items/${itemId}`, {
      method: 'DELETE',
    });
  }

  async reorderPlaylist(playlistId: string, items: { id: string; sortOrder: number }[]) {
    return this.request<{ message: string }>(`/playlists/${playlistId}/reorder`, {
      method: 'PATCH',
      body: JSON.stringify({ items }),
    });
  }

  async uploadPlaylistCover(playlistId: string, file: File) {
    const formData = new FormData();
    formData.append('cover', file);
    return this.request<{ coverImage: string }>(`/files/playlist-cover/${playlistId}`, {
      method: 'POST',
      body: formData,
      headers: {}, // Let browser set Content-Type for FormData
    });
  }

  async deletePlaylistCover(playlistId: string) {
    return this.request<void>(`/files/playlist-cover/${playlistId}`, {
      method: 'DELETE',
    });
  }

  // ========== MESSAGES ==========
  async getMessages(params?: { type?: MessageType; limit?: number; offset?: number }) {
    const searchParams = new URLSearchParams();
    if (params?.type) searchParams.set('type', params.type);
    if (params?.limit) searchParams.set('limit', String(params.limit));
    if (params?.offset) searchParams.set('offset', String(params.offset));
    const query = searchParams.toString();
    return this.request<Message[]>(`/messages${query ? `?${query}` : ''}`);
  }

  async getMessage(messageId: string) {
    return this.request<MessageDetail>(`/messages/${messageId}`);
  }

  async createMessage(data: CreateMessageData) {
    return this.request<Message>('/messages', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  async updateMessage(messageId: string, data: Partial<CreateMessageData>) {
    return this.request<Message>(`/messages/${messageId}`, {
      method: 'PATCH',
      body: JSON.stringify(data),
    });
  }

  async deleteMessage(messageId: string) {
    return this.request<void>(`/messages/${messageId}`, {
      method: 'DELETE',
    });
  }

  async toggleMessagePin(messageId: string) {
    return this.request<Message>(`/messages/${messageId}/pin`, {
      method: 'POST',
    });
  }

  async likeMessage(messageId: string) {
    return this.request<{ liked: boolean }>(`/messages/${messageId}/like`, {
      method: 'POST',
    });
  }

  async addComment(messageId: string, content: string) {
    return this.request<MessageComment>(`/messages/${messageId}/comments`, {
      method: 'POST',
      body: JSON.stringify({ content }),
    });
  }

  async deleteComment(messageId: string, commentId: string) {
    return this.request<void>(`/messages/${messageId}/comments/${commentId}`, {
      method: 'DELETE',
    });
  }

  // ========== MEDIA ==========
  async getMediaAlbums() {
    return this.request<MediaAlbum[]>('/media/albums');
  }

  async getMediaAlbum(albumId: string) {
    return this.request<MediaAlbumDetail>(`/media/albums/${albumId}`);
  }

  async createMediaAlbum(data: CreateMediaAlbumData) {
    return this.request<MediaAlbum>('/media/albums', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  async updateMediaAlbum(albumId: string, data: Partial<CreateMediaAlbumData>) {
    return this.request<MediaAlbum>(`/media/albums/${albumId}`, {
      method: 'PATCH',
      body: JSON.stringify(data),
    });
  }

  async deleteMediaAlbum(albumId: string) {
    return this.request<void>(`/media/albums/${albumId}`, {
      method: 'DELETE',
    });
  }

  async deleteMediaItem(itemId: string) {
    return this.request<void>(`/media/items/${itemId}`, {
      method: 'DELETE',
    });
  }

  async uploadMediaItem(albumId: string, formData: FormData): Promise<MediaItem> {
    const token = this.getAccessToken();
    const headers: HeadersInit = {};
    if (token) {
      headers['Authorization'] = `Bearer ${token}`;
    }
    // Don't set Content-Type - let browser set it with boundary for multipart
    const response = await fetch(`${API_BASE_URL}/media/albums/${albumId}/items`, {
      method: 'POST',
      headers,
      body: formData,
    });

    if (!response.ok) {
      const error = await response.json().catch(() => ({ error: 'Upload failed' }));
      throw new Error(error.error || 'Upload failed');
    }

    return response.json();
  }

  getMediaItemUrl(itemId: string): string {
    const token = this.getAccessToken();
    return `${API_BASE_URL}/media/items/${itemId}${token ? `?token=${token}` : ''}`;
  }

  getMediaThumbnailUrl(itemId: string): string {
    const token = this.getAccessToken();
    return `${API_BASE_URL}/media/items/${itemId}/thumbnail${token ? `?token=${token}` : ''}`;
  }

  async setAlbumCover(albumId: string, itemId: string) {
    return this.request<MediaAlbum>(`/media/albums/${albumId}/set-cover`, {
      method: 'POST',
      body: JSON.stringify({ itemId }),
    });
  }

  async generateMediaThumbnails() {
    return this.request<{ message: string; total: number; processed: number; failed: number }>('/media/generate-thumbnails', {
      method: 'POST',
    });
  }

  async reorderMediaItems(albumId: string, itemIds: string[]) {
    return this.request<{ success: boolean }>(`/media/albums/${albumId}/reorder`, {
      method: 'PATCH',
      body: JSON.stringify({ itemIds }),
    });
  }
}

export interface UserTag {
  id: string;
  name: string;
  color: string | null;
  sortOrder: number;
  userCount?: number;
}

export interface UserListItem {
  id: string;
  name: string;
  photo: string | null;
  voiceGroup: 'SOPRAAN' | 'ALT' | 'TENOR' | 'BAS' | null;
  role: Role;
  isTemporaryDirigent?: boolean;
  hasBestuurAccess?: boolean;
  hasEvenementenAccess?: boolean;
  tags?: UserTag[];
}

export interface User {
  id: string;
  email: string;
  name: string;
  role: Role;
  photo: string | null;
  voiceGroup: 'SOPRAAN' | 'ALT' | 'TENOR' | 'BAS' | null;
  isTemporaryDirigent?: boolean;
  hasBestuurAccess?: boolean;
  hasEvenementenAccess?: boolean;
}

export type Role = 'KOORLID' | 'DIRIGENT' | 'MUZIEKCOMMISSIE' | 'BESTUUR' | 'EVENEMENTEN' | 'SUPERADMIN';
export type VoiceGroup = 'SOPRAAN' | 'ALT' | 'TENOR' | 'BAS';

export interface AdminUser {
  id: string;
  email: string;
  name: string;
  role: Role;
  isTemporaryDirigent: boolean;
  hasBestuurAccess: boolean;
  hasEvenementenAccess: boolean;
  photo: string | null;
  voiceGroup: VoiceGroup | null;
  createdAt: string;
  lastLogin: string | null;
}

export interface CreateUserData {
  email: string;
  name: string;
  role?: Role;
  voiceGroup?: VoiceGroup | null;
  isTemporaryDirigent?: boolean;
  hasBestuurAccess?: boolean;
  hasEvenementenAccess?: boolean;
}

export interface UpdateUserData {
  name?: string;
  role?: Role;
  voiceGroup?: VoiceGroup | null;
  isTemporaryDirigent?: boolean;
  hasBestuurAccess?: boolean;
  hasEvenementenAccess?: boolean;
}

// ========== VOICE PART TEMPLATE TYPES ==========
export interface VoicePartTemplate {
  id: string;
  name: string;
  sortOrder: number;
  usageCount?: number;
}

// ========== SONG TYPES ==========
export type FileType = 'OEFENTRACK' | 'SONGTEKST' | 'CHOREOGRAFIE' | 'PARTITUUR' | 'BACKTRACK';

export interface Tag {
  id: string;
  name: string;
  color: string | null;
  songCount?: number;
}

export interface VoicePart {
  id: string;
  name: string;
  sortOrder: number;
  templateId?: string | null;
  template?: { id: string; name: string } | null;
  files?: SongFile[];
}

export interface SongFile {
  id: string;
  type: FileType;
  filename: string;
  originalName: string;
  size?: number;
  voicePartId: string | null;
}

export interface Bookmark {
  id: string;
  label: string | null;
  name: string;
  timestamp: number;
  omitPreRoll: boolean;
  omitFadeIn: boolean;
}

export interface Song {
  id: string;
  title: string;
  artist: string | null;
  coverImage: string | null;
  startDate: string | null;
  chronologicalOrder: number; // Deprecated
  voiceParts: VoicePart[];
  tags: Tag[];
  files: SongFile[];
  hasOfflineFiles?: boolean;
}

export interface SongDetail extends Song {
  bookmarks: Bookmark[];
  selectedVoicePartId: string | null;
}

export interface CreateSongData {
  title: string;
  artist?: string;
  startDate?: string;
  chronologicalOrder?: number; // Deprecated
  tagIds?: string[];
  voicePartTemplateIds?: string[];
  voiceParts?: { name: string; sortOrder?: number }[]; // Deprecated
}

// ========== IMPORT TYPES ==========
export interface ImportPreviewItem {
  row: number;
  title: string;
  artist: string | null;
  startDate: string | null;
  tags: string[];
  voiceParts: string[];
  isShortcode: boolean;
  errors: string[];
  warnings: string[];
}

export interface ImportPreviewResponse {
  preview: ImportPreviewItem[];
  stats: {
    total: number;
    valid: number;
    errors: number;
    warnings: number;
  };
}

export interface ImportItem {
  title: string;
  artist: string | null;
  startDate: string | null;
  tags: string[];
  voiceParts: string[];
}

export interface ImportResult {
  success: boolean;
  created: number;
  skipped: number;
  errors: { row: number; title: string; error: string }[];
}

// ========== ANYTUNE IMPORT TYPES ==========
export interface AnytuneMarker {
  timestamp: number;
  name: string;
  shortLabel?: string;
}

export interface AnytuneMarkerWithStatus extends AnytuneMarker {
  trackTitle: string;
  isDuplicate: boolean;
}

export interface AnytunePreviewResponse {
  markers: AnytuneMarkerWithStatus[];
  totalMarkers: number;
  tracks: {
    title: string;
    artist: string | null;
    markerCount: number;
  }[];
}

// ========== PLAYLIST TYPES ==========
export interface PlaylistItem {
  id: string;
  sortOrder: number;
  song: {
    id: string;
    title: string;
    artist: string | null;
    coverImage?: string | null;
    files?: { id: string; voicePartId: string | null }[];
  };
  voicePart: {
    id: string;
    name: string;
  };
}

export interface Playlist {
  id: string;
  name: string;
  coverImage: string | null;
  createdAt: string;
  updatedAt: string;
  items: PlaylistItem[];
}

// ========== MESSAGE TYPES ==========
export type MessageType = 'DIRIGENT' | 'BESTUUR' | 'MUZIEKCOMMISSIE' | 'EVENEMENTEN';

export interface MessageAuthor {
  id: string;
  name: string;
  photo: string | null;
}

export interface MessageComment {
  id: string;
  content: string;
  createdAt: string;
  author: MessageAuthor;
}

export interface Message {
  id: string;
  type: MessageType;
  title: string;
  content: string;
  isPinned: boolean;
  createdAt: string;
  updatedAt: string;
  author: MessageAuthor;
  commentCount: number;
  likeCount: number;
  isLiked: boolean;
}

export interface MessageDetail extends Message {
  comments: MessageComment[];
}

export interface CreateMessageData {
  type: MessageType;
  title: string;
  content: string;
}

// ========== MEDIA TYPES ==========
export type MediaType = 'PHOTO' | 'VIDEO';

export interface MediaAlbum {
  id: string;
  name: string;
  description: string | null;
  eventDate: string | null;
  coverImage: string | null;
  createdAt: string;
  updatedAt: string;
  itemCount: number;
  totalSize: number;
  createdBy: {
    id: string;
    name: string;
  };
}

export interface MediaItem {
  id: string;
  type: MediaType;
  filename: string;
  originalName: string;
  mimeType: string;
  size: number;
  path: string;
  thumbnailPath: string | null;
  caption: string | null;
  sortOrder: number;
  createdAt: string;
  uploadedBy: {
    id: string;
    name: string;
  };
}

export interface MediaAlbumDetail extends MediaAlbum {
  items: MediaItem[];
}

export interface CreateMediaAlbumData {
  name: string;
  description?: string;
  eventDate?: string;
}

export const api = new ApiClient();
